import React, { useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import type { RootReducerType } from "@store/store";
import {
  getDocumentsListThunk,
  postSaveUploadedFileThunk,
  getUploadedFileByIdThunk,
  getDeleteSignedURLThunk,
  submitDocumentThunk,
  deleteFileByIdThunk,
  getFileSignedURLThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { deleteFileOnAwsAPI } from "../../../utils/s3Upload";
import { UploadDocumentProps } from "@modules/stepper-row/types";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { ImagePreviewGridRow } from "@modules/application-section-row/components/ImagePreviewGridRow";
import { openFileBySignedUrl } from "../../../utils/getViewSignedUrlHandler";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { LegacyImagePreviewGridRow } from "./LegacyImagePreviewGridRow";
import CBuDInfo from "@modules/stepper-row/components/modal/CBuDInfo";

type LegacyUploadDocumentsROWProps = UploadDocumentProps & {
  setSubmitHandler: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
  setIsStepValid: (valid: boolean) => void;
};

const RowLegacyUploadDocuments: React.FC<LegacyUploadDocumentsROWProps> = ({
  collapsed,
  setSubmitHandler,
  setIsStepValid,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { category_id, access_id } = useSelector(
    (state: RootReducerType) => state.accessLegacy,
  );

  const [allDocuments, setAllDocuments] = React.useState<any[]>([]);
  const [progress, setProgress] = React.useState(0);
  const [refreshImagesFlag, setRefreshImagesFlag] = React.useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // const urlApplicationId = searchParams.get("row");
  // const applicationID = urlApplicationId || access_id;

  const fetchAndMergeDocuments = async () => {
    const documentListData = await dispatch(
      getDocumentsListThunk({
        category_id: Number(category_id),
        access_id: access_id,
        document: "file_upload",
      }),
    );

    const baseDocs = documentListData.payload.data || [];
    const uploadedFileById = await dispatch(
      getUploadedFileByIdThunk({
        access_id: access_id,
        category_id: Number(category_id),
        document: "file_upload",
      }),
    );

    const uploadedFiles = uploadedFileById.payload.data?.files || [];
    setProgress(uploadedFileById.payload.data?.completionPercentage || 0);

    const mergedDocs = baseDocs.map((doc: any) => {
      const uploadedFile =
        uploadedFiles.length > 0 &&
        uploadedFiles.find((file: any) => file.document_id === doc.document_id);
      return uploadedFile ? { ...doc, uploadedFile } : doc;
    });

    setAllDocuments(mergedDocs);
    setRefreshImagesFlag((prev) => !prev);
  };

  const handleViewFile = (doc_name: any) => {
    openFileBySignedUrl({
      document_name: doc_name,
      dispatch,
      getSignedUrlThunk: getFileSignedURLThunk,
    });
  };

  function getTruncatedFileName(fileName: string, maxLength = 15) {
    const lastDot = fileName?.lastIndexOf(".");
    if (lastDot === -1) return fileName;
    const name = fileName?.slice(0, lastDot);
    const ext = fileName?.slice(lastDot);
    const truncated =
      name?.length > maxLength ? name?.slice(0, maxLength) + "..." : name;
    return truncated + ext;
  }

  React.useEffect(() => {
    fetchAndMergeDocuments();
  }, [dispatch, category_id]);

  React.useEffect(() => {
    const isValid = progress === 100;
    setIsStepValid(isValid);

    setSubmitHandler(() => async () => {
      // if (!isValid) {
      //   toast.error("Please upload all required documents before proceeding.");
      //   return;
      // }

      const submit = await dispatch(
        submitDocumentThunk({
          access_id: access_id,
          category_id: Number(category_id),
        }),
      );

      // if (!submit.payload?.data?.isComplete) {
      //   toast.error("Documents submission not complete.");
      //   return;
      // }

      toast.success("Documents submitted successfully.");
    });

    // return () => setSubmitHandler(null);
    return () => {};
  }, [
    progress,
    category_id,
    access_id,
    dispatch,
    setIsStepValid,
    setSubmitHandler,
  ]);

  const filterpdfList = allDocuments?.filter(
    (doc) => doc.document_type === "FILE_UPLOAD" && doc.media_type === "pdf",
  );

  const filterImageList = allDocuments?.filter(
    (doc) => doc.document_type === "FILE_UPLOAD" && doc.media_type === "image",
  );

  const filterdrwList = allDocuments?.filter(
    (doc) => doc.document_type === "FILE_UPLOAD" && doc.media_type === "drw",
  );

  React.useEffect(() => {
    if (!Array.isArray(filterImageList) || filterImageList.length === 0) return;

    const lastImagePath =
      filterImageList[filterImageList.length - 1]?.uploadedFile?.upload_path;
    if (!lastImagePath) return;

    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [
    filterImageList?.[filterImageList.length - 1]?.uploadedFile?.upload_path,
  ]);

  React.useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [location.hash]);

  const renderDocumentList = () => (
    <>
      <Box className="document-header">
        <h5 className="font-semibold body-m">Drawing Type (PDF)</h5>
        <h5 className="font-semibold body-m">Action</h5>
      </Box>
      <Box className="document-list">
        {filterdrwList?.map((doc, index) => (
          <Box
            key={doc.document_id}
            className={`document-row ${index % 2 === 0 ? "even" : "odd"}`}
          >
            <Typography
              variant="body2"
              className="document-text"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {doc.document_name}{" "}
              {doc.upload_required && <span className="required">*</span>}
              {doc.document_code === "CBUD_APP3" && (
                <CBuDInfo documentId={`info-${doc.document_id}`} />
              )}
            </Typography>
            <label
              htmlFor={`upload-file-${doc.document_id}`}
              className="flex items-center gap-1"
            >
              {!doc.uploadedFile ? (
                <React.Fragment>
                  <input
                    type="file"
                    id={`upload-file-${doc.document_id}`}
                    style={{ display: "none" }}
                    accept=".pdf"
                    onChange={async (event) => {
                      const fileInput = event.target;
                      const file = fileInput.files?.[0];
                      if (file) {
                        fileInput.value = "";
                        if (file.size > 21 * 1024 * 1024) {
                          toast.error("File size must be less than 20 MB");
                          return;
                        }
                        const fileUploadURL = await dispatch(
                          genericUploadThunk({
                            file,
                            moduleType: "row",
                          }),
                        );
                        if (!fileUploadURL?.payload?.success) {
                          const details =
                            fileUploadURL?.payload?.response?.data?.details ||
                            fileUploadURL?.payload?.details;

                          toast.error(
                            Array.isArray(details)
                              ? details?.[0]
                              : typeof details === "string"
                                ? details
                                : "Something Went Wrong!",
                          );
                          return;
                        }
                        if (!productionCheck) {
                          const uploadSuccess = await dispatch(
                            uploadToS3Thunk({
                              signedUrl: fileUploadURL.payload.data.signedUrl,
                              file,
                              contentType: file.type,
                              uploadInstructions:
                                fileUploadURL.payload.data.uploadInstructions,
                            }),
                          ).unwrap();

                          if (!uploadSuccess) {
                            toast.error("File upload to S3 failed");
                            return;
                          }
                        }
                        await dispatch(
                          postSaveUploadedFileThunk({
                            application_id: access_id,
                            document_id: doc.document_id,
                            document_type: doc.document_type,
                            fileName: fileUploadURL.payload.data.fileName,
                            file_name: file.name,
                            originalFileName:
                              fileUploadURL.payload.data.fileName,
                            objectKey: fileUploadURL.payload.data.objectKey,
                            moduletype: "row",
                            isUndertaking: false,
                          }),
                        );
                        fetchAndMergeDocuments();
                        fileInput.value = "";
                      }
                    }}
                  />
                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    className="download-btn"
                    component="span"
                  >
                    <FileUploadOutlinedIcon className="download-icon" />
                  </IconButton>
                </React.Fragment>
              ) : (
                <>
                  <Typography
                    className="font-medium underline cursor-pointer body-xs"
                    sx={{
                      color: "#757575",
                      mr: 1,
                      "&:hover": {
                        color: "#3A1C73",
                        textDecoration: "underline",
                      },
                    }}
                    onClick={() =>
                      handleViewFile(doc?.uploadedFile?.document_name)
                    }
                    title={doc.file_name}
                  >
                    {getTruncatedFileName(doc?.uploadedFile?.file_name)}
                  </Typography>
                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      mx: 1,
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    className="download-btn"
                  >
                    <RemoveRedEyeOutlinedIcon
                      className="download-icon"
                      onClick={() =>
                        handleViewFile(doc?.uploadedFile?.document_name)
                      }
                    />
                  </IconButton>
                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    className="download-btn"
                  >
                    <CloseIcon
                      className="download-icon"
                      onClick={async () => {
                        try {
                          const deleteSignURL = await dispatch(
                            getDeleteSignedURLThunk(
                              doc.uploadedFile.document_name,
                            ),
                          );
                          const signedUrl =
                            deleteSignURL.payload?.data?.signedUrl;
                          if (
                            deleteSignURL.meta.requestStatus === "fulfilled"
                          ) {
                            await deleteFileOnAwsAPI(signedUrl);
                            await dispatch(
                              deleteFileByIdThunk({
                                access_id: access_id,
                                document_id: doc.uploadedFile.id,
                              }),
                            );
                            fetchAndMergeDocuments();
                          }
                        } catch (error) {
                          console.error("Error deleting file:", error);
                        }
                      }}
                    />
                  </IconButton>
                </>
              )}
            </label>
          </Box>
        ))}
      </Box>

      <Divider sx={{ marginY: 2 }} />

      <Box className="document-header">
        <h5 className="font-semibold body-m">Document Type (PDF)</h5>
        <h5 className="font-semibold body-m">Action</h5>
      </Box>
      <Box className="document-list ">
        {filterpdfList?.map((doc, index) => (
          <Box
            key={doc.document_id}
            className={`document-row ${index % 2 === 0 ? "even" : "odd"}`}
          >
            <Typography
              variant="body2"
              className="document-text"
              sx={{ display: "flex", alignItems: "center" }}
            >
              {doc.document_name}{" "}
              {doc.upload_required && <span className="required">*</span>}
              {doc.document_code === "CBUD_APP3" && (
                <CBuDInfo documentId={`info-${doc.document_id}`} />
              )}
            </Typography>
            <label
              htmlFor={`upload-file-${doc.document_id}`}
              className="flex items-center gap-1"
            >
              {!doc.uploadedFile ? (
                <React.Fragment>
                  <input
                    type="file"
                    id={`upload-file-${doc.document_id}`}
                    style={{ display: "none" }}
                    accept=".pdf"
                    onChange={async (event) => {
                      const fileInput = event.target;
                      const file = fileInput.files?.[0];
                      if (file) {
                        fileInput.value = "";
                        if (file.size > 21 * 1024 * 1024) {
                          toast.error("File size must be less than 20 MB");
                          return;
                        }

                        const fileUploadURL = await dispatch(
                          genericUploadThunk({
                            file,
                            moduleType: "row",
                          }),
                        );
                        if (!fileUploadURL?.payload?.success) {
                          const details =
                            fileUploadURL?.payload?.response?.data?.details ||
                            fileUploadURL?.payload?.details;

                          toast.error(
                            Array.isArray(details)
                              ? details?.[0]
                              : typeof details === "string"
                                ? details
                                : "Something Went Wrong!",
                          );
                          return;
                        }
                        if (!productionCheck) {
                          const uploadSuccess = await dispatch(
                            uploadToS3Thunk({
                              signedUrl: fileUploadURL.payload.data.signedUrl,
                              file,
                              contentType: file.type,
                            }),
                          ).unwrap();
                          if (!uploadSuccess) {
                            toast.error("File upload to S3 failed");
                            return;
                          }
                        }
                        await dispatch(
                          postSaveUploadedFileThunk({
                            application_id: access_id,
                            document_id: doc.document_id,
                            document_type: doc.document_type,
                            fileName: fileUploadURL.payload.data.fileName,
                            file_name: file.name,
                            originalFileName:
                              fileUploadURL.payload.data.fileName,
                            objectKey: fileUploadURL.payload.data.objectKey,
                            moduletype: "row",
                            isUndertaking: false,
                          }),
                        );
                        fetchAndMergeDocuments();
                        fileInput.value = "";
                      }
                    }}
                  />
                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    className="download-btn"
                    component="span"
                  >
                    <FileUploadOutlinedIcon className="download-icon" />
                  </IconButton>
                </React.Fragment>
              ) : (
                <>
                  <Typography
                    className="font-medium underline cursor-pointer body-xs"
                    sx={{
                      color: "#757575",
                      mr: 1,
                      "&:hover": {
                        color: "#3A1C73",
                        textDecoration: "underline",
                      },
                    }}
                    onClick={() =>
                      handleViewFile(doc?.uploadedFile?.document_name)
                    }
                    title={doc.file_name}
                  >
                    {getTruncatedFileName(doc?.uploadedFile?.file_name)}
                  </Typography>
                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      mx: 1,
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    className="download-btn"
                  >
                    <RemoveRedEyeOutlinedIcon
                      className="download-icon"
                      onClick={() =>
                        handleViewFile(doc?.uploadedFile?.document_name)
                      }
                    />
                  </IconButton>
                  <IconButton
                    sx={{
                      background: "#FFFFFF",
                      borderRadius: "8px",
                      border: "1px solid #D0D5DD",
                      padding: "6px 8px",
                      "&:hover": {
                        backgroundColor: "#E7E4F8",
                      },
                    }}
                    className="download-btn"
                  >
                    <CloseIcon
                      className="download-icon"
                      onClick={async () => {
                        try {
                          const deleteSignURL = await dispatch(
                            getDeleteSignedURLThunk(
                              doc.uploadedFile.document_name,
                            ),
                          );
                          const signedUrl =
                            deleteSignURL.payload?.data?.signedUrl;
                          if (
                            deleteSignURL.meta.requestStatus === "fulfilled"
                          ) {
                            await deleteFileOnAwsAPI(signedUrl);
                            await dispatch(
                              deleteFileByIdThunk({
                                access_id: access_id,
                                document_id: doc.uploadedFile.id,
                              }),
                            );
                            fetchAndMergeDocuments();
                          }
                        } catch (error) {
                          console.error("Error deleting file:", error);
                        }
                      }}
                    />
                  </IconButton>
                </>
              )}
            </label>
          </Box>
        ))}
      </Box>
    </>
  );

  return (
    <Box className="upload-document-wrapper" ref={containerRef}>
      <div className="progress-bar">
        <h3 className="stepper-hd w-full body-m flex items-center justify-between">
          Upload Documents
          <Box position="relative" display="flex">
            <CircularProgress
              variant="determinate"
              value={progress}
              size={40}
              thickness={4}
              sx={{
                "& .MuiCircularProgress-circle": {
                  stroke: "#2E844A",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "50%",
                  border: "4px solid #CFDAE7",
                  zIndex: -1,
                },
              }}
            />
            <Box
              top={0}
              left={0}
              bottom={0}
              right={0}
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography
                variant="caption"
                component="div"
                color="textSecondary"
              >
                {`${progress || 0}%`}
              </Typography>
            </Box>
          </Box>
        </h3>
      </div>

      {collapsed ? (
        <Grid className="collapsed-grid">{renderDocumentList()}</Grid>
      ) : (
        <Box className="upload-document-container">{renderDocumentList()}</Box>
      )}

      <Box mx={2} id="upload-plot-pictures">
        <h3
          className="stepper-hd w-full body-m flex items-center"
          style={{ paddingLeft: "2px" }}
        >
          Upload Plot Pictures<span className="required">*</span>
        </h3>
        {filterImageList?.map((doc) => (
          <Box key={doc.document_id}>
            <Box sx={{ mt: 1 }}>
              <LegacyImagePreviewGridRow
                refreshTrigger={refreshImagesFlag}
                onDeleteSuccess={() => {
                  fetchAndMergeDocuments();
                }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                mt: 3,
                border: "1px dashed #CFDAE7",
                borderRadius: "8px",
                p: 2,
                background: "#f0edf4",
                mb: 3,
                "&:hover": {
                  backgroundColor: "#E7E4F8",
                  transition: "all 0.3s ease-in-out",
                  borderColor: "#3a1c73",
                },
              }}
              component="label"
            >
              <Box component="div">
                <input
                  type="file"
                  id={`upload-file-${doc.document_id}`}
                  hidden
                  accept=".jpg,.jpeg,.png"
                  onChange={async (event) => {
                    const fileInput = event.target;
                    const file = fileInput.files?.[0];
                    if (file) {
                      fileInput.value = "";
                      const allowedTypes = ["image/jpeg", "image/png"];
                      if (!allowedTypes.includes(file.type)) {
                        toast.error(
                          "Only JPG, JPEG, and PNG files are allowed",
                        );
                        return;
                      }
                      if (file.size > 21 * 1024 * 1024) {
                        toast.error("File size must be less than 20 MB");
                        return;
                      }
                      const fileUploadURL = await dispatch(
                        genericUploadThunk({ file, moduleType: "row" }),
                      );
                      if (!fileUploadURL?.payload?.success) {
                        const details =
                          fileUploadURL?.payload?.response?.data?.details ||
                          fileUploadURL?.payload?.details;

                        toast.error(
                          Array.isArray(details)
                            ? details?.[0]
                            : typeof details === "string"
                              ? details
                              : "Something Went Wrong!",
                        );
                        return;
                      }
                      if (!productionCheck) {
                        const uploadSuccess = await dispatch(
                          uploadToS3Thunk({
                            signedUrl: fileUploadURL.payload.data.signedUrl,
                            file,
                            contentType: file.type,
                          }),
                        ).unwrap();
                        if (!uploadSuccess) {
                          throw new Error("File upload to S3 failed");
                        }
                      }
                      await dispatch(
                        postSaveUploadedFileThunk({
                          application_id: access_id,
                          document_id: doc.document_id,
                          document_type: doc.document_type,
                          fileName: fileUploadURL.payload.data.fileName,
                          file_name: file.name,
                          originalFileName: fileUploadURL.payload.data.fileName,
                          objectKey: fileUploadURL.payload.data.objectKey,
                          moduletype: "row",
                          isUndertaking: false,
                        }),
                      );
                      fetchAndMergeDocuments();
                      fileInput.value = "";
                    }
                  }}
                />
                <AddPhotoAlternateOutlinedIcon
                  style={{ fontSize: 24, color: "#3a1c73" }}
                />
              </Box>
              <Typography
                sx={{ color: "#3a1c73", fontSize: 12, cursor: "pointer" }}
              >
                Click to add Photos
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RowLegacyUploadDocuments;
